import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from '../hooks/useForm';
import { useApi } from '../hooks/useApi';
import { apiPost, apiPut } from '../utils/api';
import { formatBusinessNumber, formatBusinessManagementNumber } from '../utils/formatters';
import './CompanyFormModal.css';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const get = (obj, path) => path.split('.').reduce((o, key) => (o == null ? o : o[key]), obj);

// 등록을 막는 모든 문제를 한 번에 찾는다. 반환: [{ field, label, message }] (화면 순서대로)
const findProblems = (form) => {
  const problems = [];
  const add = (field, label, message) => problems.push({ field, label, message });
  const blank = (path) => !String(get(form, path) ?? '').trim();

  if (blank('name')) add('name', '기업명', '기업명을 입력하세요');
  if (blank('person_in_charge')) add('person_in_charge', '담당자', '담당자를 선택하세요');
  if (blank('info.representative')) add('info.representative', '대표자', '대표자를 입력하세요');
  const bizNo = String(form.info?.business_number || '').replace(/\D/g, '');
  if (!bizNo) add('info.business_number', '사업자등록번호', '사업자등록번호를 입력하세요');
  else if (bizNo.length !== 10) add('info.business_number', '사업자등록번호', `숫자 10자리여야 합니다 (지금 ${bizNo.length}자리)`);
  const mgmtNo = String(form.info?.business_registration_code || '').replace(/\D/g, '');
  if (mgmtNo && mgmtNo.length !== 11) add('info.business_registration_code', '사업장관리번호', `숫자 11자리여야 합니다 (지금 ${mgmtNo.length}자리)`);
  if (blank('info.address')) add('info.address', '주소', '주소를 입력하세요');
  if (!form.info?.payday_last_day && !form.info?.payday_next_month) {
    const day = Number(form.info?.payday);
    if (!day || day < 1 || day > 31) add('info.payday', '급여지급일', '1~31 사이의 날짜를 넣거나 말일/익월을 선택하세요');
  }
  [['manager.biz.email', '사업담당자 이메일'], ['manager.fin.email', '재무담당자 이메일']].forEach(([path, label]) => {
    const value = String(get(form, path) || '').trim();
    if (value && !EMAIL.test(value)) add(path, label, '이메일 형식이 아닙니다 (예: name@company.com)');
  });
  return problems;
};

// 서버가 돌려준 오류를 칸에 연결한다
const serverProblems = (error) => {
  const message = error.message || '알 수 없는 오류';
  if (Array.isArray(error.detail)) {
    // FastAPI 검증 오류: loc = ["body", "info", "payday"]
    return error.detail.map((d) => {
      const field = (d.loc || []).slice(1).join('.');
      return { field, label: field || '입력값', message: d.msg };
    });
  }
  if (message.includes('기업명')) return [{ field: 'name', label: '기업명', message }];
  if (message.includes('담당자')) return [{ field: 'person_in_charge', label: '담당자', message }];
  return [{ field: null, label: '서버', message }];
};

const fieldId = (field) => `cfm-${field.replace(/\./g, '-')}`;

function Field({ field, label, required, error, hint, children, wide }) {
  return (
    <div className={`cfm-field ${wide ? 'wide' : ''} ${error ? 'has-error' : ''}`}>
      <label htmlFor={fieldId(field)}>{label}{required && <span className="cfm-required"> *</span>}</label>
      {children}
      {error ? <div className="cfm-error-text">{error}</div> : hint ? <div className="cfm-hint">{hint}</div> : null}
    </div>
  );
}

function CompanyFormModal({ isOpen, onClose, onSuccess, users = [], editingCompany = null }) {
  const isEditMode = Boolean(editingCompany);
  const { values: formData, handleChange, setValues: setFormData } = useForm({});
  const { loading: saving, callApi } = useApi();
  // 등록을 한 번 누른 뒤부터는 입력할 때마다 남은 문제를 다시 보여 준다
  const [attempted, setAttempted] = useState(false);
  const [fromServer, setFromServer] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setAttempted(false);
    setFromServer([]);

    if (editingCompany) {
      // 수정 모드: 기존 데이터로 초기화
      setFormData({
        ...editingCompany,
        info: { ...(editingCompany.info || {}) },
        manager: {
          biz: { ...(editingCompany.manager?.biz || {}) },
          fin: { ...(editingCompany.manager?.fin || {}) }
        },
        contact: { ...(editingCompany.contact || {}) },
        others: { ...(editingCompany.others || {}) }
      });
    } else {
      // 신규 등록 모드: 기본값
      setFormData({
        name: '',
        alt_name: '',
        person_in_charge: '',
        info: {
          representative: '',
          business_number: '',
          business_registration_code: '',
          address: '',
          payday_last_day: false,
          payday: 25,
          payday_next_month: false
        },
        manager: {
          biz: { name: '', phone: '', email: '' },
          fin: { name: '', phone: '', email: '' }
        },
        contact: { holiday_pay: false, extra_contract: '' },
        others: { memo: '' }
      });
    }
  }, [isOpen, editingCompany, setFormData]);

  const problems = useMemo(
    () => (attempted ? [...findProblems(formData), ...fromServer] : []),
    [attempted, formData, fromServer],
  );
  const errorOf = (field) => {
    const hit = problems.find((p) => p.field === field);
    return hit ? hit.message : null;
  };

  const change = (field, value) => {
    handleChange(field, value);
    // 고친 칸의 서버 오류는 지운다(다시 보내 봐야 아는 것이므로)
    setFromServer((list) => list.filter((p) => p.field !== field));
  };

  const handleBusinessNumberChange = (value) => {
    const formatted = formatBusinessNumber(value);
    change('info.business_number', formatted);

    // 사업자등록번호가 완성되면 사업장관리번호에 자동 복사
    if (formatted.length === 12) {
      const numbers = formatted.replace(/\D/g, '');
      const managementFormatted = `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}-${numbers.slice(10) || '0'}`;
      change('info.business_registration_code', managementFormatted);
    }
  };

  // 기업명 변경 시 별칭 자동 입력
  const handleNameChange = (value) => {
    change('name', value);
    handleChange('alt_name', value);
  };

  const focusField = (field) => {
    if (!field) return;
    const el = document.getElementById(fieldId(field));
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAttempted(true);
    setFromServer([]);
    const found = findProblems(formData);
    if (found.length) {
      focusField(found[0].field);
      return;
    }

    try {
      const result = await callApi(async () => {
        if (isEditMode) {
          return await apiPut(`/api/companies/${editingCompany.id}`, formData);
        }
        return await apiPost('/api/companies/', formData);
      });

      if (result) {
        onSuccess(result);
        onClose();
      }
    } catch (error) {
      console.error(isEditMode ? '기업 수정 오류:' : '기업 등록 오류:', error);
      const list = serverProblems(error);
      setFromServer(list);
      focusField(list[0].field);
    }
  };

  if (!isOpen) return null;

  const input = (field, props = {}) => (
    <input
      id={fieldId(field)}
      value={get(formData, field) ?? ''}
      onChange={(e) => change(field, e.target.value)}
      {...props}
    />
  );

  const lastDay = Boolean(formData.info?.payday_last_day);
  const nextMonth = Boolean(formData.info?.payday_next_month);

  return (
    <div className="cfm-overlay">
      {/* noValidate: 브라우저 기본 검사는 한 칸씩만 알려 주므로 끄고, 아래에서 전부 모아 보여 준다 */}
      <form className="cfm-modal" onSubmit={handleSubmit} noValidate>
        <div className="cfm-header">
          <h3>{isEditMode ? '기업 정보 수정' : '신규 기업 등록'}</h3>
          <span className="cfm-muted"><span className="cfm-required">*</span> 필수 입력</span>
          <button type="button" className="cfm-close" onClick={onClose} title="닫기">✕</button>
        </div>

        {problems.length > 0 && (
          <div className="cfm-problems">
            <div className="cfm-problems-title">{isEditMode ? '수정' : '등록'}하려면 아래 {problems.length}곳을 확인하세요</div>
            <ul>
              {problems.map((p, i) => (
                <li key={`${p.field}-${i}`}>
                  <button type="button" onClick={() => focusField(p.field)} disabled={!p.field}>
                    <b>{p.label}</b> — {p.message}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="cfm-body">
          <section>
            <h4>기본 정보</h4>
            <div className="cfm-grid">
              <Field field="name" label="기업명" required error={errorOf('name')}>
                {input('name', { onChange: (e) => handleNameChange(e.target.value) })}
              </Field>
              <Field field="alt_name" label="기업 별칭" hint="기업명을 넣으면 같이 채워집니다">
                {input('alt_name', { onChange: (e) => handleChange('alt_name', e.target.value) })}
              </Field>
              <Field field="person_in_charge" label="담당자" required error={errorOf('person_in_charge')}>
                <select id={fieldId('person_in_charge')} value={formData.person_in_charge || ''} onChange={(e) => change('person_in_charge', e.target.value)}>
                  <option value="">담당자를 선택하세요</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{user.name} ({user.level})</option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          <section>
            <h4>사업자 정보</h4>
            <div className="cfm-grid">
              <Field field="info.representative" label="대표자" required error={errorOf('info.representative')}>
                {input('info.representative')}
              </Field>
              <Field field="info.business_number" label="사업자등록번호" required error={errorOf('info.business_number')}>
                {input('info.business_number', { placeholder: '000-00-00000', maxLength: 12, onChange: (e) => handleBusinessNumberChange(e.target.value) })}
              </Field>
              <Field field="info.business_registration_code" label="사업장관리번호" error={errorOf('info.business_registration_code')} hint="사업자등록번호를 넣으면 자동으로 채워집니다">
                {input('info.business_registration_code', { placeholder: '000-00-00000-0', maxLength: 14, onChange: (e) => change('info.business_registration_code', formatBusinessManagementNumber(e.target.value)) })}
              </Field>
              <Field field="info.address" label="주소" required wide error={errorOf('info.address')}>
                {input('info.address')}
              </Field>
              <Field field="info.payday" label="급여지급일" required wide error={errorOf('info.payday')}>
                <div className="cfm-payday">
                  <input
                    id={fieldId('info.payday')}
                    type="number"
                    min="1"
                    max="31"
                    placeholder="일"
                    value={formData.info?.payday || ''}
                    disabled={lastDay}
                    onChange={(e) => change('info.payday', parseInt(e.target.value, 10) || 0)}
                  />
                  <span>일</span>
                  <label className="cfm-check">
                    <input
                      type="checkbox"
                      checked={lastDay}
                      onChange={(e) => {
                        change('info.payday_last_day', e.target.checked);
                        if (e.target.checked) {
                          change('info.payday', 0);
                          change('info.payday_next_month', false);
                        }
                      }}
                    />
                    말일
                  </label>
                  <label className="cfm-check">
                    <input
                      type="checkbox"
                      checked={nextMonth}
                      onChange={(e) => {
                        change('info.payday_next_month', e.target.checked);
                        if (e.target.checked) change('info.payday_last_day', false);
                      }}
                    />
                    익월
                  </label>
                </div>
              </Field>
            </div>
          </section>

          <section>
            <h4>기업 측 담당자 <span className="cfm-muted">선택 입력</span></h4>
            <table className="cfm-table">
              <thead><tr><th style={{ width: 110 }} /><th>이름</th><th>전화</th><th>이메일</th></tr></thead>
              <tbody>
                {[['biz', '사업담당자'], ['fin', '재무담당자']].map(([key, label]) => (
                  <tr key={key}>
                    <th>{label}</th>
                    <td>{input(`manager.${key}.name`)}</td>
                    <td>{input(`manager.${key}.phone`)}</td>
                    <td className={errorOf(`manager.${key}.email`) ? 'has-error' : ''}>
                      {input(`manager.${key}.email`, { type: 'email' })}
                      {errorOf(`manager.${key}.email`) && <div className="cfm-error-text">{errorOf(`manager.${key}.email`)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section>
            <h4>기타 <span className="cfm-muted">선택 입력</span></h4>
            <div className="cfm-grid">
              <div className="cfm-field wide">
                <label className="cfm-check">
                  <input type="checkbox" checked={formData.contact?.holiday_pay || false} onChange={(e) => change('contact.holiday_pay', e.target.checked)} />
                  휴일수당 있음
                </label>
              </div>
              <Field field="contact.extra_contract" label="추가계약" wide hint="계약서의 [특약사항] 조항에 들어갑니다. 엔터(줄바꿈)로 항목을 구분하세요. 앞의 ①②③… 번호는 자동으로 붙으니 직접 쓰지 않아도 됩니다.">
                <textarea id={fieldId('contact.extra_contract')} rows="4" placeholder={'한 줄에 한 항목씩 입력하세요\n예) 연간 180만원 상당의 식대 보조금을 월할하여 지급한다.'} value={formData.contact?.extra_contract || ''} onChange={(e) => change('contact.extra_contract', e.target.value)} />
              </Field>
              <Field field="others.memo" label="메모" wide>
                <textarea id={fieldId('others.memo')} rows="3" placeholder="메모를 입력하세요" value={formData.others?.memo || ''} onChange={(e) => change('others.memo', e.target.value)} />
              </Field>
            </div>
          </section>
        </div>

        <div className="cfm-footer">
          {problems.length > 0 && <span className="cfm-footer-note">확인이 필요한 칸 {problems.length}곳</span>}
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>취소</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (isEditMode ? '수정 중...' : '등록 중...') : (isEditMode ? '수정' : '등록')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CompanyFormModal;
